/** LocalStorage state persistence with versioning.
 *
 * Saves and restores synth parameters, oscillator layers (with per-layer
 * ADSR/filter), MIDI mappings, tuning offsets, and link preferences.
 */

import type { SequencerNote } from "../components/Sequencer.tsx";
import type {
	ADSRParams,
	DelayParams,
	FilterParams,
	LimiterParams,
	MidiChannel,
	OscillatorLayer,
	SynthParams,
} from "../types/synth.ts";
import {
	DEFAULT_ADSR,
	DEFAULT_DELAY,
	DEFAULT_FILTER,
	DEFAULT_LAYER,
	DEFAULT_LIMITER,
	DEFAULT_PARAMS,
} from "../types/synth.ts";
import type { ArpParams } from "./Arpeggiator.ts";
import { DEFAULT_ARP } from "./Arpeggiator.ts";
import type { ChorusParams } from "./Chorus.ts";
import { DEFAULT_CHORUS } from "./Chorus.ts";
import type { MidiMapping } from "./midiMapping.ts";

/** Current schema version — bump when the shape of PersistedState changes. */
const STATE_VERSION = 8 as const;

/** The localStorage key used for persisted state. */
const STORAGE_KEY = "wasm-synth-state" as const;

/** Tuning offsets applied to note pitch. */
export type TuningState = {
	readonly octaveOffset: number;
	readonly semitoneOffset: number;
	readonly centOffset: number;
	/** When true, chord notes are auto-tuned to pure just intonation ratios. */
	readonly cleanChords: boolean;
};

/** Sequencer notes stored per synth instance ID. */
export type PersistedSequencerNotes = {
	readonly [synthId: string]: readonly SequencerNote[];
};

/** Sequencer measures stored per synth instance ID. */
export type PersistedSequencerMeasures = {
	readonly [synthId: string]: number;
};

/** A persisted synth instance (ID, name, MIDI channel, params, delay, limiter, chorus). */
export type PersistedSynthInstance = {
	readonly id: string;
	readonly name: string;
	readonly midiChannel: MidiChannel;
	readonly params: SynthParams;
	readonly delay: DelayParams;
	readonly limiter: LimiterParams;
	readonly chorus: ChorusParams;
	/** Whether this synth is muted (no audio output). */
	readonly muted: boolean;
	/** Whether this synth is soloed (only soloed synths produce sound). */
	readonly solo: boolean;
	/** Per-synth arpeggiator parameters. */
	readonly arp: ArpParams;
};

/** The full persisted state shape. */
export type PersistedState = {
	readonly version: number;
	readonly params: SynthParams;
	readonly tuning: TuningState;
	readonly midiMappings: readonly MidiMapping[];
	/** Whether ADSR changes propagate to all layers. */
	readonly adsrLinked: boolean;
	/** Whether filter changes propagate to all layers. */
	readonly filterLinked: boolean;
	/** Delay effect parameters (for the primary synth). */
	readonly delay: DelayParams;
	/** Limiter/compressor parameters (for the primary synth). */
	readonly limiter: LimiterParams;
	/** Multiple synth instances — if empty, single synth mode. */
	readonly synthInstances: readonly PersistedSynthInstance[];
	/** ID of the currently active synth instance. */
	readonly activeSynthId: string;
	/** Sequencer notes per synth instance. */
	readonly sequencerNotes: PersistedSequencerNotes;
	/** Number of sequencer measures (per synth instance). */
	readonly sequencerMeasures: PersistedSequencerMeasures;
	/** Master bus gain — global output volume (0 to 1). */
	readonly masterBusGain: number;
	/** Arpeggiator parameters. */
	readonly arp: ArpParams;
	/** Chorus effect parameters. */
	readonly chorus: ChorusParams;
};

/** Default tuning state (no offsets). */
export const DEFAULT_TUNING: TuningState = {
	octaveOffset: 0,
	semitoneOffset: 0,
	centOffset: 0,
	cleanChords: false,
} as const;

/** Build the default persisted state. */
function defaultState(): PersistedState {
	return {
		version: STATE_VERSION,
		params: DEFAULT_PARAMS,
		tuning: DEFAULT_TUNING,
		midiMappings: [],
		adsrLinked: true,
		filterLinked: true,
		delay: DEFAULT_DELAY,
		limiter: DEFAULT_LIMITER,
		synthInstances: [
			{
				id: "synth-1",
				name: "Synth 1",
				midiChannel: "all",
				params: DEFAULT_PARAMS,
				delay: DEFAULT_DELAY,
				limiter: DEFAULT_LIMITER,
				chorus: DEFAULT_CHORUS,
				muted: false,
				solo: false,
				arp: DEFAULT_ARP,
			},
		],
		activeSynthId: "synth-1",
		sequencerNotes: {},
		sequencerMeasures: {},
		masterBusGain: 0.7,
		arp: DEFAULT_ARP,
		chorus: DEFAULT_CHORUS,
	};
}

/** Ensure each layer has adsr, filter, pan, and enabled fields (for migration). */
function ensureLayerFields(layer: Record<string, unknown>): OscillatorLayer {
	return {
		id: (layer.id as string) ?? DEFAULT_LAYER.id,
		waveform:
			(layer.waveform as OscillatorLayer["waveform"]) ?? DEFAULT_LAYER.waveform,
		semitoneOffset: (layer.semitoneOffset as number) ?? 0,
		centOffset: (layer.centOffset as number) ?? 0,
		gain: (layer.gain as number) ?? 1.0,
		pan: (layer.pan as number) ?? 0,
		adsr: (layer.adsr as ADSRParams) ?? DEFAULT_ADSR,
		filter: (layer.filter as FilterParams) ?? DEFAULT_FILTER,
		enabled: (layer.enabled as boolean) ?? true,
		wavetableId: (layer.wavetableId as string | undefined) ?? undefined,
		customWavetable:
			(layer.customWavetable as readonly number[] | undefined) ?? undefined,
	};
}

function normalizeTempo(value: unknown): number {
	return typeof value === "number" && Number.isFinite(value)
		? value
		: DEFAULT_PARAMS.tempo;
}

/** Minimal shape check — does this object look like a persisted state? */
function looksLikeState(obj: unknown): boolean {
	if (typeof obj !== "object" || obj === null) return false;
	const s = obj as Record<string, unknown>;
	if (typeof s.version !== "number") return false;
	if (typeof s.params !== "object" || s.params === null) return false;
	const p = s.params as Record<string, unknown>;
	if (typeof p.masterGain !== "number") return false;
	return true;
}

/** Migrate state from an older version to the current one. */
function migrate(raw: Record<string, unknown>): PersistedState {
	const version = typeof raw.version === "number" ? raw.version : 0;
	const base = defaultState();

	if (version < 1) {
		// Very old or unknown — return defaults, try to keep midi mappings
		return {
			...base,
			midiMappings: Array.isArray(raw.midiMappings) ? raw.midiMappings : [],
		};
	}

	if (version < 2) {
		// v1 → v2: top-level waveform/adsr/filter move into each layer
		const params = raw.params as Record<string, unknown> | undefined;
		const globalAdsr = (params?.adsr as ADSRParams) ?? DEFAULT_ADSR;
		const globalFilter = (params?.filter as FilterParams) ?? DEFAULT_FILTER;
		const globalWaveform =
			(params?.waveform as OscillatorLayer["waveform"]) ?? "sawtooth";
		const masterGain =
			typeof params?.masterGain === "number" ? params.masterGain : 0.7;

		// Migrate each layer to include adsr/filter
		let layers: OscillatorLayer[];
		if (
			Array.isArray(params?.oscillatorLayers) &&
			(params.oscillatorLayers as unknown[]).length > 0
		) {
			layers = (params.oscillatorLayers as Record<string, unknown>[]).map(
				(l) => ({
					...ensureLayerFields(l),
					// Inherit global adsr/filter if layer didn't have them
					adsr: (l.adsr as ADSRParams) ?? globalAdsr,
					filter: (l.filter as FilterParams) ?? globalFilter,
				}),
			);
		} else {
			layers = [
				{
					...DEFAULT_LAYER,
					waveform: globalWaveform,
					adsr: globalAdsr,
					filter: globalFilter,
				},
			];
		}

		return {
			version: STATE_VERSION,
			params: {
				masterGain,
				oscillatorLayers: layers,
				pan: 0,
				tempo: DEFAULT_PARAMS.tempo,
			},
			tuning: (raw.tuning as TuningState) ?? DEFAULT_TUNING,
			midiMappings: Array.isArray(raw.midiMappings) ? raw.midiMappings : [],
			adsrLinked: true,
			filterLinked: true,
			delay: DEFAULT_DELAY,
			limiter: DEFAULT_LIMITER,
			synthInstances: [],
			activeSynthId: "",
			sequencerNotes: {},
			sequencerMeasures: {},
			masterBusGain: 0.7,
			arp: DEFAULT_ARP,
			chorus: DEFAULT_CHORUS,
		};
	}

	if (version < 3) {
		// v2 → v3: add sequencer notes and measures
		const v2 = raw as unknown as Omit<
			PersistedState,
			"sequencerNotes" | "sequencerMeasures" | "masterBusGain"
		>;
		return {
			...v2,
			version: STATE_VERSION,
			sequencerNotes: {},
			sequencerMeasures: {},
			masterBusGain: 0.7,
		} as PersistedState;
	}

	if (version < 4) {
		// v3 → v4: add master bus gain
		const v3 = raw as unknown as Omit<
			PersistedState,
			"masterBusGain" | "limiter"
		>;
		return migrate({
			...v3,
			version: 4,
			masterBusGain: 0.7,
		} as unknown as Record<string, unknown>);
	}

	if (version < 5) {
		// v4 → v5: add limiter params
		const v4 = raw as unknown as Omit<PersistedState, "limiter">;
		const instances = Array.isArray(
			(raw as Record<string, unknown>).synthInstances,
		)
			? (
					(raw as Record<string, unknown>)
						.synthInstances as PersistedSynthInstance[]
				).map((inst) => ({
					...inst,
					limiter: (inst as Record<string, unknown>).limiter
						? (inst as unknown as PersistedSynthInstance).limiter
						: DEFAULT_LIMITER,
				}))
			: [];
		return migrate({
			...v4,
			version: 5,
			limiter: DEFAULT_LIMITER,
			synthInstances: instances,
		} as unknown as Record<string, unknown>);
	}

	if (version < 6) {
		// v5 → v6: sequencerMeasures changes from number to per-synth map
		const v5 = raw as Record<string, unknown>;
		const oldMeasures =
			typeof v5.sequencerMeasures === "number" ? v5.sequencerMeasures : 4;
		// Distribute the global measure count to every synth that has notes
		const seqNotes = (v5.sequencerNotes ?? {}) as PersistedSequencerNotes;
		const measuresMap: Record<string, number> = {};
		for (const synthId of Object.keys(seqNotes)) {
			measuresMap[synthId] = oldMeasures;
		}
		// Also ensure the active synth has an entry
		const activeId =
			typeof v5.activeSynthId === "string" ? v5.activeSynthId : "synth-1";
		if (!measuresMap[activeId]) {
			measuresMap[activeId] = oldMeasures;
		}
		return migrate({
			...(v5 as unknown as PersistedState),
			version: 6,
			sequencerMeasures: measuresMap as PersistedSequencerMeasures,
			arp: defaultState().arp,
			chorus: defaultState().chorus,
		} as unknown as Record<string, unknown>);
	}

	if (version < 7) {
		// v6 → v7: add pan property to layers and synth params
		const v6 = raw as Record<string, unknown>;
		const v6Params = v6.params as Record<string, unknown>;
		const v6Layers = v6Params.oscillatorLayers as readonly Record<
			string,
			unknown
		>[];
		const v6Instances = (v6.synthInstances ?? []) as readonly Record<
			string,
			unknown
		>[];

		const addPanToLayers = (layers: readonly Record<string, unknown>[]) =>
			layers.map((l) => ({
				...l,
				pan: typeof l.pan === "number" ? l.pan : 0,
			}));

		const params = {
			...v6Params,
			pan: typeof v6Params.pan === "number" ? v6Params.pan : 0,
			oscillatorLayers: addPanToLayers(v6Layers),
		};
		const synthInstances = v6Instances.map((inst) => {
			const instParams = (inst.params ?? {}) as Record<string, unknown>;
			const instLayers = (instParams.oscillatorLayers ?? []) as readonly Record<
				string,
				unknown
			>[];
			return {
				...inst,
				params: {
					...instParams,
					pan: typeof instParams.pan === "number" ? instParams.pan : 0,
					oscillatorLayers: addPanToLayers(instLayers),
				},
			};
		});
		return migrate({
			...v6,
			version: 7,
			params,
			synthInstances,
		} as unknown as Record<string, unknown>);
	}

	if (version < 8) {
		// v7 → v8: wavetableId and customWavetable are optional fields on layers.
		// No structural changes required — just bump the version.
		return {
			...(raw as unknown as PersistedState),
			version: STATE_VERSION,
		};
	}

	if (version < 8) {
		// v7 → v8: add cleanChords field to tuning state
		const v7 = raw as Record<string, unknown>;
		const v7Tuning = (v7.tuning ?? {}) as Record<string, unknown>;
		return {
			...v7,
			version: STATE_VERSION,
			tuning: {
				...v7Tuning,
				cleanChords: false,
			},
		} as unknown as PersistedState;
	}

	return raw as unknown as PersistedState;
}

/** Load persisted state from localStorage, falling back to defaults.
 *  Checks the URL for shared state first — if present, it takes priority.
 */
export function loadState(): PersistedState {
	// URL-shared state takes priority
	const urlState = loadStateFromURL();
	if (urlState) {
		// Persist the URL state locally so it survives page reload
		saveState(urlState);
		// Clear the URL param so it doesn't override future edits
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete(URL_STATE_PARAM);
			window.history.replaceState(null, "", url.toString());
		} catch {
			// Non-browser environment — ignore
		}
		return urlState;
	}

	try {
		const json = localStorage.getItem(STORAGE_KEY);
		if (!json) return defaultState();

		const raw = JSON.parse(json) as Record<string, unknown>;
		if (!looksLikeState(raw)) return migrate(raw);

		if ((raw.version as number) < STATE_VERSION) return migrate(raw);

		// Ensure all layers have adsr/filter (defensive)
		const state = raw as unknown as PersistedState;
		const layers = state.params.oscillatorLayers.map((l) =>
			ensureLayerFields(l as unknown as Record<string, unknown>),
		);
		const normalizedParams: SynthParams = {
			...state.params,
			pan: typeof state.params.pan === "number" ? state.params.pan : 0,
			tempo: normalizeTempo(state.params.tempo),
			oscillatorLayers: layers,
		};
		const normalizedInstances =
			Array.isArray(state.synthInstances) && state.synthInstances.length > 0
				? state.synthInstances.map((inst) => ({
						...inst,
						params: {
							...inst.params,
							pan: typeof inst.params?.pan === "number" ? inst.params.pan : 0,
							tempo: normalizeTempo(inst.params?.tempo),
							oscillatorLayers: inst.params?.oscillatorLayers
								? inst.params.oscillatorLayers.map((l: OscillatorLayer) =>
										ensureLayerFields(l as unknown as Record<string, unknown>),
									)
								: layers,
						},
						midiChannel: inst.midiChannel ?? ("all" as MidiChannel),
						name: inst.name ?? "Synth",
						delay: inst.delay ?? state.delay ?? DEFAULT_DELAY,
						limiter:
							(inst as unknown as Record<string, unknown>).limiter ??
							(state as unknown as Record<string, unknown>).limiter ??
							DEFAULT_LIMITER,
						chorus:
							(inst as unknown as Record<string, unknown>).chorus ??
							defaultState().chorus,
						muted: (inst as unknown as Record<string, unknown>).muted === true,
						solo: (inst as unknown as Record<string, unknown>).solo === true,
						arp: (inst as unknown as Record<string, unknown>).arp
							? (inst as unknown as { readonly arp: ArpParams }).arp
							: DEFAULT_ARP,
					}))
				: [
						{
							id: "synth-1",
							name: "Synth 1",
							midiChannel: "all" as MidiChannel,
							params: normalizedParams,
							delay: state.delay ?? DEFAULT_DELAY,
							limiter:
								(state as unknown as Record<string, unknown>).limiter ??
								DEFAULT_LIMITER,
							chorus: defaultState().chorus,
							muted: false,
							solo: false,
							arp: DEFAULT_ARP,
						},
					];

		return {
			...state,
			params: normalizedParams,
			tuning: {
				...DEFAULT_TUNING,
				...state.tuning,
			},
			adsrLinked: state.adsrLinked ?? true,
			filterLinked: state.filterLinked ?? true,
			delay: state.delay ?? DEFAULT_DELAY,
			limiter:
				((state as unknown as Record<string, unknown>).limiter as
					| LimiterParams
					| undefined) ?? DEFAULT_LIMITER,
			synthInstances: normalizedInstances,
			activeSynthId: state.activeSynthId ?? "synth-1",
			sequencerNotes: state.sequencerNotes ?? {},
			sequencerMeasures:
				typeof state.sequencerMeasures === "object" &&
				state.sequencerMeasures !== null
					? state.sequencerMeasures
					: {},
			masterBusGain: (state as PersistedState).masterBusGain ?? 0.7,
			arp: (state as unknown as Record<string, unknown>).arp
				? (state.arp as PersistedState["arp"])
				: defaultState().arp,
			chorus: (state as unknown as Record<string, unknown>).chorus
				? (state.chorus as PersistedState["chorus"])
				: defaultState().chorus,
		};
	} catch {
		return defaultState();
	}
}

/** Debounce timer ID for batched localStorage writes. */
let saveTimerId: ReturnType<typeof setTimeout> | null = null;

/** Debounce interval in milliseconds — writes are batched within this window. */
const SAVE_DEBOUNCE_MS = 150 as const;

/** Pending state waiting to be flushed to localStorage. */
let pendingState: PersistedState | null = null;

/** Flush any pending debounced state to localStorage immediately. */
export function flushState(): void {
	if (saveTimerId !== null) {
		clearTimeout(saveTimerId);
		saveTimerId = null;
	}
	if (pendingState !== null) {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingState));
		} catch {
			// Quota exceeded or private browsing — silently ignore
		}
		pendingState = null;
	}
}

/** Save state to localStorage (debounced — batches rapid writes). */
export function saveState(state: PersistedState): void {
	pendingState = state;
	if (saveTimerId !== null) {
		clearTimeout(saveTimerId);
	}
	saveTimerId = setTimeout(flushState, SAVE_DEBOUNCE_MS);
}

/** Convenience: save only the synth params portion. */
export function saveParams(
	params: SynthParams,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, params };
	saveState(next);
	return next;
}

/** Convenience: save only the tuning portion. */
export function saveTuning(
	tuning: TuningState,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, tuning };
	saveState(next);
	return next;
}

/** Convenience: save only the MIDI mappings portion. */
export function saveMappings(
	mappings: readonly MidiMapping[],
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, midiMappings: mappings };
	saveState(next);
	return next;
}

/** Convenience: save the link preferences. */
export function saveLinked(
	adsrLinked: boolean,
	filterLinked: boolean,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, adsrLinked, filterLinked };
	saveState(next);
	return next;
}

/** Convenience: save only the delay parameters. */
export function saveDelay(
	delay: DelayParams,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, delay };
	saveState(next);
	return next;
}

/** Convenience: save only the limiter parameters. */
export function saveLimiter(
	limiter: LimiterParams,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, limiter };
	saveState(next);
	return next;
}

/** Convenience: save multi-synth instance list + active ID. */
export function saveSynthInstances(
	instances: readonly PersistedSynthInstance[],
	activeSynthId: string,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = {
		...current,
		synthInstances: instances,
		activeSynthId,
	};
	saveState(next);
	return next;
}

/** Convenience: save sequencer notes and measures. */
export function saveSequencer(
	sequencerNotes: PersistedSequencerNotes,
	sequencerMeasures: PersistedSequencerMeasures,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = {
		...current,
		sequencerNotes,
		sequencerMeasures,
	};
	saveState(next);
	return next;
}

/** Convenience: save arpeggiator parameters. */
export function saveArp(
	arp: PersistedState["arp"],
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, arp };
	saveState(next);
	return next;
}

/** Convenience: save chorus parameters. */
export function saveChorus(
	chorus: PersistedState["chorus"],
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, chorus };
	saveState(next);
	return next;
}

/** Convenience: save master bus gain. */
export function saveMasterBusGain(
	masterBusGain: number,
	current: PersistedState,
): PersistedState {
	const next: PersistedState = { ...current, masterBusGain };
	saveState(next);
	return next;
}

/** URLSearchParams key used for shared state. */
const URL_STATE_PARAM = "state" as const;

/** Encode the current persisted state into a shareable URL string. */
export function stateToShareURL(state: PersistedState): string {
	const json = JSON.stringify(state);
	const encoded = btoa(
		encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_m, p1) =>
			String.fromCharCode(Number.parseInt(p1 as string, 16)),
		),
	);
	const url = new URL(window.location.href);
	url.searchParams.set(URL_STATE_PARAM, encoded);
	// Remove hash if present
	url.hash = "";
	return url.toString();
}

/** Try to load state from the current URL's search params.
 *  Returns the decoded state or null if not present / invalid.
 */
export function loadStateFromURL(): PersistedState | null {
	try {
		const params = new URLSearchParams(window.location.search);
		const encoded = params.get(URL_STATE_PARAM);
		if (!encoded) return null;

		const json = decodeURIComponent(
			Array.from(atob(encoded))
				.map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`)
				.join(""),
		);
		const raw = JSON.parse(json) as Record<string, unknown>;
		if (!looksLikeState(raw)) return null;
		if ((raw.version as number) < STATE_VERSION) return migrate(raw);
		return raw as unknown as PersistedState;
	} catch {
		return null;
	}
}
